/**
 * 監査ログ `/audit`。
 *
 * 追記専用であることを画面で示すため、行に `…` メニューを出さない（削除も編集も無い）。
 * ハッシュ連鎖の検証は**サーバー計算の結果**を表示するだけ。SPA で SHA-256 を
 * 再計算すると、canonical JSON の実装が 2 つになって必ずズレる。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Download, Link2, ShieldCheck } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CopyableCode,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  KeyValue,
  PageHeader,
  StatusBadge,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { ACTOR_TYPE, labelOf } from '@mail-sv/shared';
import { api, download } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter, sinceToIso } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { JsonBlock } from '../components/code-block.js';
import { ListEmpty, QueryError, RefetchingDot, errorMessageOf } from '../components/states.js';
import { RID } from './ids.js';
import type { AuditEntry, AuditVerifyResult } from '../types/api.js';

const routeApi = getRouteApi(RID.audit);

/**
 * ハッシュ連鎖の系統（`audit_logs.scope`）の表示名。
 * 値は `platform` / `org:<orgId>` / `node:<nodeId>` の 3 形（R85）。
 * ID は解決しない（1 行ごとに名前を引くと一覧が N+1 になる）。
 */
function scopeLabel(scope: string): string {
  if (scope === 'platform') return 'プラットフォーム';
  if (scope.startsWith('org:')) return `契約組織 ${scope.slice(4)}`;
  if (scope.startsWith('node:')) return `ノード ${scope.slice(5)}`;
  return scope;
}

export function AuditPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canVerify = usePermission(OPS_PERMISSION.auditVerify);
  const [selected, setSelected] = React.useState<AuditEntry | null>(null);

  const list = useCursorList<AuditEntry>({
    path: '/audit',
    keyFor: (params) => qk.audit(params),
    filters: {
      q: search.q,
      actorType: search.actorType,
      action: search.action,
      targetId: search.targetId,
      targetType: search.targetType,
      scope: search.scope,
      organizationId: search.organizationId,
      from: sinceToIso(search.since),
    },
  });

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const columns: Array<DataTableColumn<AuditEntry>> = [
    { id: 'at', header: '日時', width: 'w-44', cell: (e) => formatDateTime(e.occurredAt) },
    {
      id: 'actor',
      header: 'アクター',
      cell: (e) => (
        <div className="min-w-0">
          <Badge variant="muted" size="sm">
            {labelOf(e.actorType)}
          </Badge>
          <span className="ml-1.5 truncate text-sm">{e.actorLabel}</span>
        </div>
      ),
    },
    { id: 'action', header: '操作', cell: (e) => <code className="font-mono text-xs">{e.action}</code> },
    {
      id: 'target',
      header: '対象',
      hideBelow: 'md',
      cell: (e) => (
        <span className="text-xs text-muted-foreground">
          {e.targetType ? `${e.targetType} / ` : ''}
          {e.targetId ?? '—'}
        </span>
      ),
    },
    { id: 'ip', header: 'IP', hideBelow: 'lg', cell: (e) => <span className="font-mono text-xs">{e.ip ?? '—'}</span> },
    {
      id: 'result',
      header: '結果',
      cell: (e) => <StatusBadge status={e.success ? 'succeeded' : 'failed'} size="sm" />,
    },
    {
      id: 'chain',
      header: '連鎖',
      align: 'center',
      cell: (e) =>
        e.chainOk ? (
          <span title="ハッシュ連鎖は正常です" aria-label="連鎖正常" className="text-success">
            ✓
          </span>
        ) : (
          <span title="ハッシュ連鎖が壊れています" aria-label="連鎖異常" className="text-destructive">
            ⚠
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="監査ログ"
        description="追記専用の記録。編集・削除はできません。行をクリックすると差分とハッシュを確認できます。"
        actions={
          <>
            <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
            {canVerify ? <VerifyChainButton since={search.since} /> : null}
            <Button
              variant="outline"
              onClick={() =>
                void download('/audit/export', `audit-${new Date().toISOString().slice(0, 10)}.ndjson`, {
                  q: search.q,
                  actorType: search.actorType,
                  action: search.action,
                  // `GET /audit/export` が受けるのはここまで。**`targetType` は受けない**
                  // ので送らない（送っても黙って捨てられ、画面と中身がズレる）。
                  // 受けないことは画面側にも出す（下の注意書き）。
                  scope: search.scope,
                  organizationId: search.organizationId,
                  from: sinceToIso(search.since),
                })
              }
              title={
                search.targetType
                  ? '対象種別の絞り込みはエクスポートに反映されません（API が受け付けません）'
                  : undefined
              }
            >
              <Download aria-hidden />
              エクスポート
            </Button>
          </>
        }
      />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="アクター名 / 対象 ID で絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: { since: '7d' } })}
        selects={[
          {
            key: 'actorType',
            label: 'アクター',
            value: search.actorType,
            options: optionsFrom(ACTOR_TYPE, labelOf),
            onChange: (v) => setSearch({ actorType: v as typeof search.actorType }),
          },
          {
            key: 'since',
            label: '期間',
            value: search.since,
            allLabel: '7 日',
            options: [
              { value: '24h', label: '24 時間' },
              { value: '7d', label: '7 日' },
              { value: '30d', label: '30 日' },
              { value: '90d', label: '90 日' },
            ],
            onChange: (v) => setSearch({ since: (v ?? '7d') as typeof search.since }),
          },
          {
            key: 'scope',
            label: '系統',
            /*
             * ハッシュ連鎖の系統（R85）。`platform` は固定値なので選択肢に出せるが、
             * `org:<ID>` / `node:<ID>` は ID を含むので**列挙できない**。
             * URL に入っている値をそのまま 1 件の選択肢として足し、
             * 行の詳細パネルから「この系統だけ表示」で入れる導線を用意する
             * （運営者に ID を手打ちさせない）。
             */
            value: search.scope,
            options:
              search.scope && search.scope !== 'platform'
                ? [
                    { value: 'platform', label: 'プラットフォーム' },
                    { value: search.scope, label: scopeLabel(search.scope) },
                  ]
                : [{ value: 'platform', label: 'プラットフォーム' }],
            onChange: (v) => setSearch({ scope: v }),
          },
        ]}
      >
        <div className="w-44">
          <Input
            value={search.action ?? ''}
            onChange={(e) => setSearch({ action: e.target.value || undefined })}
            placeholder="操作（node.create など）"
            aria-label="操作で絞り込む"
            className="h-9 font-mono text-xs"
          />
        </div>
        <div className="w-40">
          <Input
            value={search.targetType ?? ''}
            onChange={(e) => setSearch({ targetType: e.target.value || undefined })}
            placeholder="対象種別（mailbox など）"
            aria-label="対象種別で絞り込む"
            className="h-9 font-mono text-xs"
          />
        </div>
      </FilterBar>

      {search.targetType ? (
        <p className="mb-4 text-xs text-muted-foreground">
          対象種別で絞り込んでいます。
          <strong className="font-medium text-warning"> エクスポートにはこの条件が反映されません</strong>
          （<code className="font-mono">GET /audit/export</code> が対象種別を受け付けないため）。
        </p>
      ) : null}

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(e) => e.id}
        loading={list.query.isLoading}
        onRowClick={setSelected}
        selectedKey={selected?.id ?? null}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="記録"
            onClearFilter={() => void navigate({ search: { since: '7d' } })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <AuditDetailPanel
        entry={selected}
        onClose={() => setSelected(null)}
        onFilter={(patch) => {
          setSelected(null);
          setSearch(patch);
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------ 連鎖の検証 */

function VerifyChainButton({ since }: { since: string }): React.ReactElement {
  const [result, setResult] = React.useState<AuditVerifyResult | null>(null);
  const mut = useMutation({
    mutationFn: () => api.post<AuditVerifyResult>('/audit/verify', { from: sinceToIso(since) }),
    onSuccess: setResult,
  });

  return (
    <>
      <Button variant="outline" onClick={() => mut.mutate()} loading={mut.isPending}>
        <ShieldCheck aria-hidden />
        連鎖を検証
      </Button>

      <Dialog open={result !== null || mut.isError} onOpenChange={() => setResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>ハッシュ連鎖の検証結果</DialogTitle>
          </DialogHeader>
          {mut.isError ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMessageOf(mut.error)}</AlertDescription>
            </Alert>
          ) : result ? (
            <Alert variant={result.ok ? 'success' : 'destructive'}>
              <AlertTitle>{result.ok ? '改竄は検出されませんでした' : '連鎖が壊れています'}</AlertTitle>
              <AlertDescription>
                <p>{formatCount(result.checked)} 件を検証しました。</p>
                {!result.ok && result.firstBrokenAt ? (
                  <p className="mt-1">
                    最初の不整合: {formatDateTime(result.firstBrokenAt)}（ID: {result.firstBrokenId ?? '不明'}）
                  </p>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------- 詳細パネル */

/**
 * 右からスライドするパネル。Radix Dialog の位置指定だけを上書きして作る
 * （フォーカストラップ・ESC・aria を自前で書き直さないため）。
 */
function AuditDetailPanel({
  entry,
  onClose,
  onFilter,
}: {
  entry: AuditEntry | null;
  onClose: () => void;
  /** 一覧のフィルタを書き換える（系統・対象種別で辿るため）。 */
  onFilter: (patch: { scope?: string; targetType?: string }) => void;
}): React.ReactElement | null {
  if (!entry) return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="left-auto right-0 top-0 h-full max-h-full w-full max-w-xl translate-x-0 translate-y-0 rounded-none rounded-l-xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{entry.action}</DialogTitle>
          <DialogDescription>{formatDateTime(entry.occurredAt)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto">
          <KeyValue
            columns={2}
            items={[
              { key: 'アクター種別', value: labelOf(entry.actorType) },
              { key: 'アクター', value: entry.actorLabel },
              {
                /*
                 * 系統と対象種別は**そこから辿れて初めて意味がある**。
                 * `scope` は `org:<ID>` の形なので、運営者に手で打たせない
                 * （API は完全一致なので 1 文字違えば黙って 0 件になる）。
                 */
                key: '系統',
                value: (
                  <button
                    type="button"
                    onClick={() => onFilter({ scope: entry.scope })}
                    className="rounded text-left text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title="この系統の記録だけを一覧に出す"
                  >
                    {scopeLabel(entry.scope)}
                  </button>
                ),
              },
              { key: '連番', value: <span className="font-mono text-xs">{entry.seq}</span> },
              {
                key: '対象種別',
                value: entry.targetType ? (
                  <button
                    type="button"
                    onClick={() => onFilter({ targetType: entry.targetType })}
                    className="rounded text-left font-mono text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title="この対象種別の記録だけを一覧に出す"
                  >
                    {entry.targetType}
                  </button>
                ) : (
                  '—'
                ),
              },
              { key: '対象 ID', value: entry.targetId ?? '—' },
              { key: 'IP', value: entry.ip ?? '—' },
              { key: '結果', value: <StatusBadge status={entry.success ? 'succeeded' : 'failed'} size="sm" /> },
              {
                key: '契約組織',
                value: entry.organizationId ? (
                  <Link
                    to="/organizations/$orgId"
                    params={{ orgId: entry.organizationId }}
                    search={{ tab: 'overview' }}
                    className="hover:text-primary"
                  >
                    {entry.organizationId}
                  </Link>
                ) : (
                  '—'
                ),
              },
              {
                key: '連鎖',
                value: entry.chainOk ? (
                  <Badge variant="success" size="sm">
                    <Link2 aria-hidden />
                    正常
                  </Badge>
                ) : (
                  <Badge variant="destructive" size="sm">
                    異常
                  </Badge>
                ),
              },
            ]}
          />

          <div className="space-y-3">
            <CopyableCode label="prevHash" value={entry.prevHash} />
            <CopyableCode label="hash" value={entry.hash} />
          </div>

          {entry.before !== null && entry.before !== undefined ? (
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">変更前</p>
              <JsonBlock value={entry.before} maxHeight="14rem" />
            </div>
          ) : null}
          {entry.after !== null && entry.after !== undefined ? (
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">変更後</p>
              <JsonBlock value={entry.after} maxHeight="14rem" />
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
